<template>
  <q-card
    class="rounded-xl border border-neutral-100/5 shadow-sm p-24 space-y-24 md2:p-16"
  >
    <TextIcon
      icon="svguse:#icon_table"
      text="Resumo de Desempenho por Categoria"
      icon-class="text-primary-pure"
      div-class="!gap-12 !font-normal"
    />

    <OTable
      ref="tableRef"
      :columns="allColumns"
      :rows="rows"
      :pagination="{ rowsPerPage: 0 }"
    >
      <template #header="props">
        <q-tr :props="props">
          <q-th
            v-for="col in props.cols"
            :key="col.name"
            :props="props"
            :class="{
              'border-l': col.name.startsWith('dynamic_col_'),
            }"
          >
            <template v-if="col.name.startsWith('dynamic_col_')">
              <OSelect
                size="sm"
                v-model="selectedColumnOption"
                :options="comparationItems"
                use-input
                map-options
                option-label="label"
                @update:model-value="onColumnOptionChange"
                class="min-w-160 bg-white"
              />
            </template>
            <template v-else-if="col.name === 'gap_bench_latam'">
              {{ col.label }}
              <q-icon
                v-if="latamFabName"
                name="help"
                size="1.125rem"
                class="text-neutral-70 cursor-pointer ml-4"
              >
                <q-tooltip
                  anchor="center right"
                  self="center left"
                  class="bg-[#202227] text-[#9F9FA0] p-12"
                >
                  {{ latamFabName }}
                </q-tooltip>
              </q-icon>
            </template>
            <template v-else>
              {{ col.label }}
            </template>
          </q-th>
          <q-th auto-width class="!px-8">
            <OButton
              v-if="!hasExtraColumn"
              size="sm"
              :loading="columnLoading"
              primary
              icon="add"
              class="size-[2rem] !min-h-0 !p-0"
            >
              <MenuPerformanceByCategory
                @update:param="onUpdateParam"
              />
            </OButton>

            <OButton
              v-else
              size="sm"
              :loading="columnLoading"
              secondary
              icon="remove"
              class="size-[2rem] !min-h-0 !p-0"
              @click="removeColumn"
            />
          </q-th>
        </q-tr>
      </template>
      <template #body="_props">
        <q-tr :props="_props" class="!h-[3.625rem]">
          <q-td key="category">
            {{ _props.row.category }}
          </q-td>
          <q-td
            key="possible_points"
            auto-width
            class="min-w-[6.25rem]"
          >
            {{ _props.row.possible_points }}
          </q-td>
          <q-td
            key="current_points"
            auto-width
            class="min-w-[6.25rem]"
          >
            {{ _props.row.current_points }}
          </q-td>
          <q-td
            key="gap_oportunity"
            auto-width
            class="min-w-[6.25rem]"
          >
            {{ _props.row.gap_oportunity }}
          </q-td>
          <q-td key="gap_bench_latam" auto-width class="w-[6.25rem]">
            {{ _props.row.gap_bench_latam }}
          </q-td>
          <q-td
            v-for="col in dynamicColumns"
            :key="col.name"
            auto-width
            class="min-w-[6.25rem] border-l"
          >
            {{ _props.row[col.name] }}
          </q-td>
          <q-td auto-width class="!px-8">
            <!-- Célula vazia para alinhar com o botão do header -->
          </q-td>
        </q-tr>
      </template>
      <template #bottom />
    </OTable>

    <template v-if="showExtraCards">
      <div
        v-for="item in quickWinsHighlightsData"
        :key="item.fab_code"
        class="border border-neutral-100/10 rounded-md"
      >
        <div
          class="text-title-3 !text-[1.25rem] !font-normal h-80 py-8 px-24 flex items-center gap-16 justify-between"
        >
          <div class="flex items-center gap-8">
            <h1 v-if="item?.isPriority">{{ item?.fab_name }}</h1>

            <OSelectPaginated
              v-else
              label="Selecione um fabricante"
              :model-value="item.selectedBottler"
              :update-on-focus="true"
              :adictional-filters="{
                fab_tier: filterParams.fab_tier,
                fab_tier_type: filterParams.fab_tier_type,
                fab_zone: filterParams.fab_zone,
                t: 'global',
              }"
              api-url="copa/api/filters/bottlers/"
              option-label="fab_name"
              option-value="fab_code"
              class="bg-white"
              @update:model-value="
                (val) => {
                  item.selectedBottler = val.fab_code
                  onSelectBottler(val.fab_code, index)
                }
              "
            />
          </div>
          <q-spinner-tail
            v-if="quickWinsHighlightsLoading"
            color="primary"
            size="1.25rem"
          />
        </div>

        <div
          v-if="item?.showCards !== false"
          class="flex flex-col gap-24 p-24 !pt-0 md2:gap-8"
        >
          <q-card
            class="rounded-xl border border-neutral-100/5 shadow-sm p-16"
          >
            <CardMyQuickWins :title-small="true" :item="item" />
          </q-card>

          <q-card
            class="rounded-xl border border-neutral-100/5 shadow-sm p-16"
          >
            <CardMyHighlights :title-small="true" :item="item" />
          </q-card>
        </div>
      </div>
    </template>
  </q-card>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import OTable from 'components/Table/OTable.vue'
import TextIcon from 'components/Text/TextIcon.vue'
import OButton from 'components/Button/OButton.vue'
import OSelect from 'components/Select/OSelect.vue'
import CardMyQuickWins from './CardMyQuickWins.vue'
import CardMyHighlights from './CardMyHighlights.vue'
import MenuPerformanceByCategory from './MenuPerformanceByCategory.vue'
import useCopaReports from 'composables/useCopaReports'
import {
  filterModel,
  filterParams,
  shouldRemoveDynamicColumns,
  comparationItems,
  quickWinsHighlightsData,
  quickWinsHighlightsLoading,
} from 'store/copa.stores'
import OSelectPaginated from 'components/Select/OSelectPaginated.vue'

const {
  fetchOverview,
  updateQuickWinsHighlights,
  selectBottlerForQuickWins,
} = useCopaReports()

const tableRef = ref(null)
const dynamicColumnCount = ref(0)
const selectedColumnOption = ref(null)

// Atualiza os valores da coluna dinâmica baseado na opção selecionada
const onColumnOptionChange = (option) => {
  fetchNewColumnData(option, false)
}

const columns = ref([
  {
    name: 'category',
    label: 'Categoria',
    align: 'left',
  },
  {
    name: 'possible_points',
    label: 'Pontos Possíveis',
    align: 'left',
  },
  {
    name: 'current_points',
    label: 'Pontos Atual',
    align: 'left',
  },
  {
    name: 'gap_oportunity',
    label: 'Gap Oportunity',
    align: 'left',
  },
  {
    name: 'gap_bench_latam',
    label: 'Gap Bench Latam',
    align: 'left',
  },
])

const rows = ref([])

const dynamicColumns = ref([])
const latamFabName = ref(null)

const hasExtraColumn = computed(() => dynamicColumns.value.length > 0)

const allColumns = computed(() => [
  ...columns.value,
  ...dynamicColumns.value,
])

const columnLoading = ref(false)

async function fetchNewColumnData(selectedOpt, addColumn = true) {
  const formatNumber = (num) => {
    if (Number.isInteger(num)) return num
    return Number(num?.replace(',', '.'))
  }

  // Busca dados com o filtro selecionado
  columnLoading.value = true
  const filteredResponse = await fetchOverview({
    [selectedOpt.filter]: selectedOpt.value,
  })
  columnLoading.value = false

  let newColumnName

  if (addColumn) {
    // Adiciona a nova coluna
    dynamicColumnCount.value++
    newColumnName = `dynamic_col_${dynamicColumnCount.value}`

    dynamicColumns.value.push({
      name: newColumnName,
      label: selectedOpt.label,
      filter: selectedOpt.filter,
      align: 'left',
    })
  } else {
    // Usa a coluna existente e atualiza seus dados
    newColumnName = dynamicColumns.value[0]?.name
    if (!newColumnName) return

    // Atualiza o filter e label da coluna existente
    dynamicColumns.value[0].filter = selectedOpt.filter
    dynamicColumns.value[0].label = selectedOpt.label
  }

  // Calcula a diferença e adiciona valores para cada row
  rows.value.forEach((row, index) => {
    const value1 = formatNumber(
      filteredResponse.rows[index].current_points
    )

    const value2 = formatNumber(rows.value[index].current_points)
    const difference = value1 - value2
    const formatedDifference = difference.toFixed(1).replace('.', ',')

    row[newColumnName] = `${formatedDifference}`
  })
}

const removeColumn = () => {
  if (dynamicColumns.value.length === 0) return

  const columnToRemove = dynamicColumns.value[0]

  // Remove os valores da coluna de cada row
  rows.value.forEach((row) => {
    delete row[columnToRemove.name]
  })

  // Remove a coluna
  dynamicColumns.value = []
  selectedColumnOption.value = null
}

const onUpdateParam = (param) => {
  selectedColumnOption.value = param
  fetchNewColumnData(param)

  // Se o parâmetro for fab_code, atualiza QuickWins e Highlights
  if (param.filter === 'fab_code') {
    const dashboardFabCode = filterParams.value.fab_code || null
    updateQuickWinsHighlights(dashboardFabCode, true)
  }
}

function fetchInitialData(params = {}) {
  removeColumn()

  fetchOverview(params).then((overview) => {
    if (!overview) return

    latamFabName.value = overview.fabName
    rows.value = overview.rows
  })
}

// Assistir mudanças na variável de remoção de colunas dinâmicas
watch(shouldRemoveDynamicColumns, (shouldRemove) => {
  if (shouldRemove) {
    removeColumn()
    // Reset da variável após remover
    shouldRemoveDynamicColumns.value = false
  }
})

/**
 * Extra cards
 */
const showExtraCards = computed(
  () =>
    filterModel.value?.fabricante?.fab_code ||
    dynamicColumns.value.find((col) => col.filter === 'fab_code') ||
    (quickWinsHighlightsData.value?.length > 0 &&
      quickWinsHighlightsData.value.some(
        (item) => item.fab_zone || item.fab_code
      ))
)

const onSelectBottler = async (selectedBottler, itemIndex) => {
  if (!selectedBottler) return
  await selectBottlerForQuickWins(selectedBottler, itemIndex)
}

// Computed para obter o fab_code da coluna dinâmica
const dynamicColumnFabCode = computed(() => {
  const fabCodeColumn = dynamicColumns.value.find(
    (col) => col.filter === 'fab_code'
  )
  return fabCodeColumn ? selectedColumnOption.value?.value : null
})

defineExpose({
  fetchInitialData,
  dynamicColumnFabCode,
})
</script>

<style scoped lang="sass">
:deep(.q-table thead tr th)
  @apply text-headline-3 bg-[#fafafa] text-neutral-70 px-12

:deep(.q-table tbody tr td)
  @apply text-headline-3 text-neutral-100 min-w-[13.75rem] px-12 first:text-paragraph-3 last:min-w-0

:deep(.q-table thead tr th.border-l),
:deep(.q-table tbody tr td.border-l)
  border-left: 1px solid rgba(0, 0, 0, 0.12) !important

:deep(.q-select.q-field--float .q-field__label)
  background: #FFF !important
  padding: 0 .25rem !important
  transform: translateY(-60%) translateX(1px) scale(0.75) !important
</style>
